import { IPropertyCard, IPropertyListRequest } from '@/components/templates/search/interfaces';
import {
	CaseReducer,
	createAsyncThunk,
	createSlice,
	PayloadAction,
} from '@reduxjs/toolkit';
import { STATUS } from '../globalContext/globalContext.interfaces';
import { IPropertyContext } from '../globalContext/propertiesContext.interface';
import { IThunkApiExtra, RootState } from '../store';


type State = IPropertyContext

export const initialState: State = {
	page: 1,
	results: [],
	allResults: [],
	batchStartPage: 1,
	total: 0,
	status: STATUS.init,
	message: undefined,
	polygon: null,
}

export const fetchByCriteria = createAsyncThunk<State, IPropertyListRequest, { extra: IThunkApiExtra; state: RootState }>(
	'properties/fetchByCriteria',
	async (request: IPropertyListRequest | undefined, thunkApi) => {
		const { rejectWithValue, extra, signal, getState } = thunkApi
		if (request === undefined) return rejectWithValue("Se necesita información para buscar propiedades");
		const { services } = extra;
		const servicesList = (await services()).setupServices()
		// Solo se manda el email si el usuario está logeado (no basta con ser invitado) —
		// así Seeker puede devolver `statistics` con los favoritos reales de ese usuario.
		const { isLogin, userData } = getState().SessionState ?? {};
		const requestWithEmail = isLogin && userData?.email ? { ...request, email: userData.email } : request;
		return (await servicesList.getPropiedades(requestWithEmail, undefined, signal)) as State
	}
)

const PAGE_SIZE = 20;
const CLIENT_PAGES_PER_BATCH = 25; // 500 resultados / 20 por página

const setResultsReducer: CaseReducer<State, PayloadAction<IPropertyCard[]>> = (state, action) =>
{
	state.results = action.payload
}

const setTotalReducer: CaseReducer<State, PayloadAction<number>> = (state, action) =>
{
	state.total = action.payload
}

const setStatusToIdleReducer: CaseReducer<State> = (state) =>
{
	state.status = STATUS.idle
}

const showPageReducer: CaseReducer<State, PayloadAction<number>> = (state, action) =>
{
	const clientPage = action.payload;
	const localPage = clientPage - state.batchStartPage + 1;
	state.results = (state.allResults ?? []).slice((localPage - 1) * PAGE_SIZE, localPage * PAGE_SIZE);
}

export const PropertiesSlice = createSlice({
	name: 'properties',
	initialState,
	reducers: {
		setResults: setResultsReducer,
		setTotal: setTotalReducer,
		setStatusToIdle: setStatusToIdleReducer,
		showPage: showPageReducer,
	},
	extraReducers: builder => {
		builder.addCase(fetchByCriteria.pending, (state, action) => {
			state.status = STATUS.pending;
			state.lastRequestId = action.meta.requestId;
		})
		builder.addCase(fetchByCriteria.fulfilled, (state, action) => {
			// Descartar respuestas fuera de orden: si llegó una request más nueva mientras
			// esta estaba en vuelo, lastRequestId ya fue sobrescrito por esa request en `pending`.
			if (action.meta.requestId !== state.lastRequestId) return;
			const { results, total, message } = action.payload;
			// Guardar siempre el array completo — el mapa muestra lo que retornó el endpoint, sin distinción por tamaño
			state.allResults = results;
			if (results.length > PAGE_SIZE) {
				const batchNumber = action.meta.arg?.page ?? 1;
				state.batchStartPage = (batchNumber - 1) * CLIENT_PAGES_PER_BATCH + 1;
			} else {
				state.batchStartPage = 1;
			}
			state.results = results.slice(0, PAGE_SIZE);
			state.total = total;
			state.message = message;
			state.polygon = action.payload.polygon ?? null;
			state.status = message !== undefined ? STATUS.error : STATUS.idle
		})
		builder.addCase(fetchByCriteria.rejected, (state, action) => {
			if (action.meta.aborted) return; // request cancelado intencionalmente, ignorar
			if (action.meta.requestId !== state.lastRequestId) return; // respuesta fuera de orden, ignorar
			state.status = STATUS.error;
			state.message = action.error ? action.error.message: "Ha ocurrido un error";
		})
	}
})

export const { setResults, setTotal, setStatusToIdle, showPage } = PropertiesSlice.actions;

export const selectPropertiesResult = (state: RootState) => state.PropertyState.results;
export const selectPolygon = (state: RootState) => state.PropertyState.polygon ?? null;
export const selectPropertiesPage = (state: RootState) => state.PropertyState.page;
export const selectPropertiesCount = (state: RootState) => state.PropertyState.total;
export const selectPropertiesStatus = (state: RootState) => state.PropertyState.status;
export const selectAllResults = (state: RootState) => state.PropertyState.allResults ?? [];
export const selectAllResultsCount = (state: RootState) => state.PropertyState.allResults?.length ?? 0;
export const selectBatchStartPage = (state: RootState) => state.PropertyState.batchStartPage ?? 1;